import {ChangeEvent, FC, useEffect, useMemo, useRef, useState} from 'react';

import {Box, Checkbox, IconButton, styled, Tooltip, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';
import {Else, If, Then, When} from 'react-if';

import {DeleteIconWithBg} from 'assets/icons/deleteIconWIthBg';
import DeleteOutlineIcon2 from 'assets/icons/deleteOutlineIcon2';
import {MinusIconWithBg} from 'assets/icons/minusIconWIthBg';
import {PlusIconWithBg} from 'assets/icons/plusIconWIthBg';
import {AddToCartButton} from 'components/add-to-cart-button/AddToCartButton';
import UpchargeBanner from 'components/upcharge-banner/UpchargeBanner';
import theme from 'config/theme';
import {BASKET_UPDATE_ITEM_SOURCE_EVENTS, BASKET_UPDATE_ITEM_TRIGGER_TYPE} from 'contexts/analytics-context';
import {useAppDispatch, useAppSelector} from 'hooks';
import {useAppMode} from 'hooks/useAppMode';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {getBasketCalculationAction, setSelectedItemsAction} from 'store/actions/basket-action';
import {AnalyticsData, BasketLineItem, Product, ProductType} from 'types';
import {formatPrice, getCurrencySymbol} from 'utils';
import {isSyncBasketEnabled, safeToFixed} from 'utils/helper';
import DiscountPriceBox from 'views/discount-price-box/DiscountPriceBox';
import {UpchargePopup} from 'views/upcharge-popup/UpchargePopup';
import {WeightedFreshPopup} from 'views/weighted-fresh-popup/WeightedFreshPopup';

import {
  ProductAttributesBox,
  QuantityAdjustedText,
  QuantitySectionStyled,
  StyledAddToCartButtonWrapper,
  StyledCard,
  StyledCardActions,
  StyledCardContent,
  StyledCardContentWrapper,
  AvailableProductsTitle,
  StyledTitleTypographyWrapper,
  StyledTooltipText
} from './styles';
import {WeightedFreshPerUnitPrice} from './WeightedFreshPerUnitPrice';
import {BasketProductImage} from '../../components/basket-product-image/BasketProductImage';
import {zIndexes} from '../../constants';
import ComplexPromotionTag from '../basket-promotions/ComplexPromotionTag';

export const QuantityPickerContainer = styled(Box)<{isReactNativeView?: boolean}>(({theme, isReactNativeView}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: `1px solid ${theme.palette.primary.main}`,
  padding: isReactNativeView ? `${theme.spacing(0.87)} 2px` : `${theme.spacing(0.87)} ${theme.spacing(2)}`,
  borderRadius: theme.spacing(4.5),
  overflow: isReactNativeView ? 'none' : 'hidden',
  '& > button:hover': {
    color: theme.palette.common.black
  },
  height: theme.spacing(2.5),
  fontSize: theme.spacing(2.5),
  width: isReactNativeView ? '112px' : 'auto',
  '&.PDPRevamp': {
    border: 'none',
    backgroundColor: theme.palette.grey[50]
  }
}));

const StyledInputField = styled(Box)<{isReactNativeWebView?: boolean}>(({theme, isReactNativeWebView}) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  '& input': {
    border: 0,
    outline: 'none',
    maxWidth: theme.spacing(5),
    textAlign: 'center',
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
    fontSize: isReactNativeWebView ? theme.spacing(1.5) : theme.spacing(2),
    paddingLeft: theme.spacing(0.5),
    '&.PDPRevamp': {
      backgroundColor: theme.palette.grey[50]
    }
  },
  '& span': {
    marginLeft: theme.spacing(0.5), // Adjust space between input and span
    fontSize: isReactNativeWebView ? '8px' : theme.spacing(1.25),
    fontWeight: 400,
    color: '#6B6B6B'
  }
}));
export const BgIconHolder = styled(Box)<{isReactNativeView?: boolean}>(({theme, isReactNativeView}) => ({
  cursor: 'pointer',
  display: 'flex',
  position: 'relative',
  '&.left': {
    left: isReactNativeView ? theme.spacing(0) : theme.spacing(-1.875)
  },
  '&.right': {
    right: isReactNativeView ? theme.spacing(0) : theme.spacing(-1.875)
  }
}));

export type BasketProductProps = {
  coverImageURL: string;
  title: string;
  index: number;
  weight: string;
  priceUnit: string;
  originPrice: number;
  displayPrice: number;
  quantity: number;
  moq: number;
  onQuantityChange: (qty: number, analyticsData?: AnalyticsData) => Promise<void>;
  productId: string;
  variantId: string;
  basketItem: BasketLineItem;
  loading: boolean;
  maxQuantity: number;
  isProductUnavailable?: boolean;
  productUnavailableText?: string;
  promotedPrice?: number;
  shownQuantityReduceSign?: boolean;
  productType: ProductType;
  upchargePercentage: number;
  upchargeMinimumThreshold: number;
  product?: Product;
  productCappedLimit: number;
  remainingQuantity: number;
  isQuantityAdjusted: boolean;
  isAlcoUnavailableSection?: boolean;
  originPriceLineTotal: number;
  displayPriceLineTotal: number;
  promotedPriceLineTotal: number;
};
export const BasketProduct: FC<BasketProductProps> = ({
  title,
  coverImageURL,
  onQuantityChange,
  quantity,
  priceUnit,
  originPrice,
  displayPrice,
  weight,
  productId,
  basketItem,
  loading,
  maxQuantity,
  isProductUnavailable,
  productUnavailableText = '',
  promotedPrice,
  productType,
  upchargePercentage,
  upchargeMinimumThreshold,
  moq,
  productCappedLimit,
  variantId,
  remainingQuantity,
  isQuantityAdjusted,
  isAlcoUnavailableSection,
  originPriceLineTotal,
  displayPriceLineTotal,
  promotedPriceLineTotal
}) => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const {isExpress} = useAppMode();

  const [quantityValue, setQuantityValue] = useState(0);
  const [isQuantityUpdatedToMax, setIsQuantityUpdatedToMax] = useState(false);
  const [quantityLoading, setQuantityLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showUpchargeModal, setShowUpchargeModal] = useState(false);
  const [showWeightedFreshModal, setShowWeightedFreshModal] = useState(false);

  const [isInputFocused, setIsInputFocused] = useState(false);

  const showUpchargeBanner = !basketItem.productDetails.isWeightedFresh && basketItem.productDetails.isUpchargeApplied;

  const {variantIdsOfItemThatWasAdjustedQuantity} = useAppSelector((state) => state?.basket);
  const showWeightedFreshBanner =
    basketItem.productDetails.isWeightedFresh && upchargePercentage > 0 && quantity >= upchargeMinimumThreshold;

  const shownQuantityReduceSign = variantIdsOfItemThatWasAdjustedQuantity.includes(variantId);
  const isSyncBasket = isSyncBasketEnabled();
  const storeSelecteditems = useAppSelector((state) => state?.basket?.selectedItems);
  const localeSelectedItems = JSON.parse(localStorage.getItem('selectedItems') || '[]') || [];
  const selectedItems = isSyncBasket ? storeSelecteditems : localeSelectedItems;
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const currencySymbol = getCurrencySymbol();

  const timer = useRef<any>(undefined);
  const analyticsData = {
    sourceEvent: '',
    triggerType: '',
    productId: basketItem?.productId || '',
    productPrice: basketItem?.productDetails?.displayPrice || '',
    qty: 0
  };

  const isWeightedFresh = basketItem?.productDetails?.isWeightedFresh || false;
  const weightedFreshPerUnitPrice = basketItem?.productDetails?.weightedFreshPricePerUnit;
  const showKg = isWeightedFresh;

  const quantityLimit = showKg ? quantity * moq : quantity;
  const minimumQuantity = showKg ? moq : 1;
  const selectedQuantity = showKg ? quantityValue * moq : quantityValue;
  const DecrementIconWithBg = selectedQuantity > minimumQuantity ? MinusIconWithBg : DeleteIconWithBg;

  const displayPriceForDiscountPriceBox = promotedPrice ? promotedPrice : displayPrice;
  const displayPriceLineTotalForDiscountPriceBox = promotedPriceLineTotal
    ? promotedPriceLineTotal
    : displayPriceLineTotal;
  const isQuantityMultipleOfMoq = Number.isInteger(quantityValue / moq);
  const otherThanCappedMessage =
    !isQuantityMultipleOfMoq && !isQuantityUpdatedToMax
      ? 'message.quantityAdjustedMessage'
      : 'message.maxQuantityReached';
  const debounceFunction = (main: () => void) => {
    return setTimeout(main, 1500);
  };

  const {weight: measuredWeight, weightUnit} = basketItem.productDetails?.measurements || {};
  const productEstimatedPcs = basketItem?.productDetails?.quantitySetByUser;

  const productUnitPropsDescription =
    measuredWeight && weightUnit && isExpress
      ? `${weight} | ${formatPrice(basketItem.productDetails.displayPrice)} ${getCurrencySymbol()} | ${basketItem.productDetails?.measurements?.weight} ${basketItem.productDetails?.measurements?.weightUnit}. / ${t('unit')}`
      : `${weight} | ${displayPriceForDiscountPriceBox && `${displayPriceForDiscountPriceBox} ${currencySymbol}`}`;

  const isCappedLimitReached = remainingQuantity === 0;
  const disableAddIcon = quantityLoading || isQuantityUpdatedToMax || isCappedLimitReached;

  useEffect(() => {
    if (quantity) {
      setQuantityValue(quantity);
      setIsQuantityUpdatedToMax(quantity === maxQuantity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity, isQuantityAdjusted]);

  useEffect(() => {
    if (isInputFocused && showKg) {
      setQuantityValue(quantity * moq);
    } else {
      setQuantityValue(quantity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInputFocused]);

  useEffect(() => {
    if (remainingQuantity === 0 && isQuantityAdjusted) {
      setShowTooltip(true);
      setTimeout(() => {
        setShowTooltip(false);
      }, 3000);
    }
  }, [remainingQuantity, isQuantityAdjusted]);

  const handleQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.currentTarget.value);
    if (!isNaN(value)) {
      if (value > maxQuantity) {
        setShowTooltip(true);
        setQuantityValue(maxQuantity);
        setIsQuantityUpdatedToMax(true);
        setTimeout(() => {
          setShowTooltip(false);
        }, 3000);
      } else {
        setQuantityValue(value);
        setIsQuantityUpdatedToMax(value === maxQuantity);
      }
    }
  };

  const onSingleItemAdjustment = async (quantity: number) => {
    if (quantity < 1 || quantity > maxQuantity) {
      return;
    }
    try {
      setQuantityLoading(true);
      setIsQuantityUpdatedToMax(quantity === maxQuantity);
      if (quantity === maxQuantity) {
        setShowTooltip(true);
        setTimeout(() => {
          setShowTooltip(false);
        }, 3000);
      }
      let sourceEvent = null;
      if (quantity > basketItem?.quantity) {
        sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.INCREASE_ITEM;
      } else if (quantity < basketItem?.quantity) {
        sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.DECREASE_ITEM;
      }
      if (sourceEvent) {
        analyticsData.qty = quantity;
        analyticsData.triggerType = BASKET_UPDATE_ITEM_TRIGGER_TYPE.BUTTON;
        analyticsData.sourceEvent = sourceEvent;
        await onQuantityChange(quantity, analyticsData);
      } else {
        await onQuantityChange(quantity);
      }
    } catch {
      // ignored
      setQuantityValue(basketItem.quantity);
    } finally {
      setQuantityLoading(false);
    }
  };

  const onFocus = () => {
    showKg && setIsInputFocused(true);
  };

  const onQuantityInputBlur = async () => {
    try {
      setQuantityLoading(true);
      let sourceEvent = null;
      if (quantityValue > basketItem?.quantity) {
        sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.INCREASE_ITEM;
      } else if (quantityValue < basketItem?.quantity) {
        if (quantityValue === 0) {
          sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.DELETE_ITEM;
        } else {
          sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.DECREASE_ITEM;
        }
      }
      if (sourceEvent) {
        analyticsData.qty = quantityValue;
        analyticsData.triggerType = BASKET_UPDATE_ITEM_TRIGGER_TYPE.KEYBOARD;
        analyticsData.sourceEvent = sourceEvent;
        if (showKg) {
          await onQuantityChange(Math.round(quantityValue / moq), analyticsData);
          if (!isQuantityMultipleOfMoq) {
            setShowTooltip(true);
            setTimeout(() => {
              setShowTooltip(false);
            }, 3000);
          }
          setQuantityValue(quantityValue);
        } else {
          await onQuantityChange(quantityValue, analyticsData);
        }
      } else {
        await onQuantityChange(quantity);
      }
      setIsInputFocused(false);
      setIsQuantityUpdatedToMax(quantityValue === maxQuantity);
    } catch {
      setIsInputFocused(false);
      setQuantityValue(quantity);
    } finally {
      setQuantityLoading(false);
    }
  };

  const formattedValueForNewInputUI = useMemo(() => {
    if (showKg) {
      return `${Number.isInteger(moq) ? quantityValue * moq : safeToFixed(quantityValue * moq)}`;
    } else {
      return Number(quantityValue);
    }
  }, [showKg, quantityValue, moq]);

  const handleDelete = async () => {
    analyticsData.sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.DELETE_ITEM;
    analyticsData.triggerType = BASKET_UPDATE_ITEM_TRIGGER_TYPE.BUTTON;
    analyticsData.qty = 0;
    await onQuantityChange(0, analyticsData);
    SendBrowserEvent.showInfoToast(t('message.productRemoved'));
    // await deleteLineItem(variantId, basketItem, analyticsData, undefined);
  };

  const updateQuantity = (action: 'increment' | 'decrement') => {
    const latestQuantity = action == 'increment' ? quantityValue + 1 : quantityValue - 1;

    const isWithinMaxQuantityLimit = action == 'increment' ? latestQuantity <= maxQuantity : Boolean(latestQuantity);
    if (isWithinMaxQuantityLimit) {
      clearTimeout(timer.current);
      setQuantityValue(latestQuantity);
      timer.current = debounceFunction(() => onSingleItemAdjustment(latestQuantity));
    }
  };

  return (
    <StyledCard isReactNativeView={isReactNativeView} data-test-product-id={productId} data-test-product-name={title}>
      <Box display="flex" gap={isReactNativeView ? '8px' : '0px'}>
        <When condition={!isProductUnavailable}>
          <Box width="5%" minWidth={'25px'} paddingRight={isReactNativeView ? '0px' : '16px'}>
            <Checkbox
              name="isDefault"
              onChange={(e) => {
                if (e.target.checked) {
                  dispatch(setSelectedItemsAction([...selectedItems, productId]));
                  !isSyncBasket && localStorage.setItem('selectedItems', JSON.stringify([...selectedItems, productId]));
                } else if (e.target.checked === false) {
                  const filteredItems = selectedItems?.filter((item: string) => item !== productId);
                  dispatch(setSelectedItemsAction(filteredItems));
                  !isSyncBasket && localStorage.setItem('selectedItems', JSON.stringify(filteredItems));
                }
                dispatch(getBasketCalculationAction());
              }}
              defaultChecked={isAlcoUnavailableSection ? false : selectedItems?.includes(productId)}
              checked={isAlcoUnavailableSection ? false : selectedItems?.includes(productId)}
              disabled={loading || isAlcoUnavailableSection}
              data-test-id={`${productId}_product_card_checkbox`}
              sx={{
                '&.Mui-disabled': {color: theme.palette.primary.light},
                color: '#C7C7C7',
                '&.Mui-checked': {
                  color: 'primary'
                },
                height: 'fit-content',
                padding: 0
              }}
            />
          </Box>
        </When>
        <Box width="95%">
          <Box display="flex" gap={isReactNativeView ? '8px' : '0px'}>
            <Box
              onClick={() => {
                SendBrowserEvent.goToPDP(productId);
              }}
            >
              <BasketProductImage
                src={coverImageURL}
                productType={productType}
                isBulky={basketItem.productDetails.isBulky}
              />
            </Box>

            <StyledCardContentWrapper role="button" tabIndex={0} onKeyDown={() => null}>
              <Box display="flex" alignItems={'flex-start'} justifyContent="space-between">
                <Box>
                  <StyledCardContent
                    isReactNativeView={isReactNativeView}
                    onClick={() => {
                      SendBrowserEvent.goToPDP(productId);
                    }}
                  >
                    <StyledTitleTypographyWrapper>
                      <AvailableProductsTitle
                        color="secondary"
                        gutterBottom
                        variant="body1"
                        component="p"
                        isReactNativeView={isReactNativeView}
                      >
                        <b>{title}</b>
                      </AvailableProductsTitle>
                    </StyledTitleTypographyWrapper>
                    <ProductAttributesBox>
                      <If condition={isWeightedFresh && weightedFreshPerUnitPrice}>
                        <Then>
                          <WeightedFreshPerUnitPrice
                            perUnitPrice={weightedFreshPerUnitPrice}
                            size={weight}
                            isWeightedFreshV3Enabled={isWeightedFresh}
                          ></WeightedFreshPerUnitPrice>
                        </Then>
                        <Else>
                          <Typography display={'block'} variant="caption" color="text.secondary">
                            {productUnitPropsDescription}
                          </Typography>
                        </Else>
                      </If>
                    </ProductAttributesBox>
                  </StyledCardContent>
                  <ComplexPromotionTag basketItem={basketItem} />
                </Box>
                <When condition={!isReactNativeView}>
                  <IconButton
                    data-test-delete-btn=""
                    disabled={loading}
                    type="button"
                    onClick={handleDelete}
                    size="small"
                  >
                    <DeleteOutlineIcon2 fillColor="#8E8E8E" fontSize="small" />
                  </IconButton>
                </When>
              </Box>
            </StyledCardContentWrapper>
          </Box>

          <StyledCardActions isReactNativeView={isReactNativeView}>
            <Box>
              <DiscountPriceBox
                originPrice={originPriceLineTotal}
                priceUnit={priceUnit}
                displayPrice={displayPriceLineTotalForDiscountPriceBox}
              />
              <When condition={showKg}>
                <Typography variant="caption" color="#212124" fontSize={12} fontWeight={400}>
                  {productEstimatedPcs === 1
                    ? t('weightedFresh.estimatedPc', {estimatedPcs: productEstimatedPcs})
                    : t('weightedFresh.estimatedPcs', {estimatedPcs: productEstimatedPcs})}
                </Typography>
              </When>
            </Box>

            {isProductUnavailable ? (
              <StyledAddToCartButtonWrapper>
                <AddToCartButton
                  disableRipple={true}
                  loading={loading}
                  disabled={true}
                  title={productUnavailableText}
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  onAdd={() => {}}
                />
              </StyledAddToCartButtonWrapper>
            ) : (
              <QuantitySectionStyled isReactNativeWebView={isReactNativeView}>
                {shownQuantityReduceSign && (
                  <QuantityAdjustedText isReactNativeWebView={isReactNativeView}>
                    {t('message.quantityAdjusted')}
                  </QuantityAdjustedText>
                )}
                <QuantityPickerContainer className={'PDPRevamp'} isReactNativeView={isReactNativeView}>
                  <BgIconHolder
                    className="left"
                    data-test-id={quantityValue > 1 ? `${productId}_decrement_quantity` : `${productId}_remove_quanity`}
                    onClick={() => {
                      if (!quantityLoading)
                        selectedQuantity > minimumQuantity ? updateQuantity('decrement') : handleDelete();
                    }}
                    isReactNativeView={isReactNativeView}
                  >
                    <DecrementIconWithBg disabled={quantityLoading} />
                  </BgIconHolder>
                  <StyledInputField isReactNativeWebView={isReactNativeView}>
                    <input
                      disabled={quantityLoading}
                      onChange={handleQuantityChange}
                      onBlur={() => {
                        clearTimeout(timer.current);
                        timer.current = debounceFunction(() => onQuantityInputBlur());
                      }}
                      data-test-id={`${productId}_product_quantity_input`}
                      onFocus={onFocus}
                      value={isInputFocused && showKg ? quantityValue : formattedValueForNewInputUI}
                      name={'quantity'}
                      autoComplete="off"
                      inputMode={showKg ? 'decimal' : 'numeric'}
                      type={'text'}
                      className={'PDPRevamp'}
                    />
                    {showKg && !isInputFocused && <span>{t('labels.Kg')}</span>}
                  </StyledInputField>

                  <Tooltip
                    title={
                      <StyledTooltipText>
                        <Trans
                          t={t}
                          components={{1: <br />}}
                          i18nKey={
                            isQuantityAdjusted && !!productCappedLimit
                              ? 'message.orderCappingMessage'
                              : otherThanCappedMessage
                          }
                          values={{
                            quantity: isQuantityAdjusted && !!productCappedLimit ? quantityLimit : moq
                          }}
                        />
                      </StyledTooltipText>
                    }
                    arrow
                    placement="top-end"
                    leaveDelay={3000}
                    open={(showTooltip && disableAddIcon) || (showTooltip && !isQuantityMultipleOfMoq)}
                    onOpen={() => setShowTooltip(false)}
                    onClose={() => {
                      setShowTooltip(true);
                    }}
                    PopperProps={{style: {zIndex: zIndexes.maxQuantityReached}}}
                  >
                    <BgIconHolder
                      className="right"
                      onClick={() => {
                        !disableAddIcon && updateQuantity('increment');
                      }}
                      color={'primary'}
                      data-test-id={`${productId}_increment_quantity`}
                      isReactNativeView={isReactNativeView}
                    >
                      <PlusIconWithBg disabled={disableAddIcon} />
                    </BgIconHolder>
                  </Tooltip>
                </QuantityPickerContainer>
              </QuantitySectionStyled>
            )}
          </StyledCardActions>
        </Box>
      </Box>

      <When condition={showUpchargeBanner}>
        <UpchargeBanner
          setShowUpchargeModal={setShowUpchargeModal}
          upchargePercentage={upchargePercentage ?? 0}
          isWeightedFresh={false}
        />
      </When>

      <When condition={showWeightedFreshBanner}>
        <UpchargeBanner
          setShowUpchargeModal={setShowWeightedFreshModal}
          upchargePercentage={upchargePercentage ?? 0}
          isWeightedFresh={true}
        />
      </When>

      <UpchargePopup
        showUpchargeModal={showUpchargeModal}
        setShowUpchargeModal={setShowUpchargeModal}
        coverImageURL={coverImageURL}
        title={title}
        productType={productType}
        weight={weight}
        priceUnit={priceUnit}
        originPrice={originPrice}
        displayPrice={displayPrice}
        promotedPrice={promotedPrice}
        upchargePercentage={upchargePercentage}
        upchargeMinimumThreshold={upchargeMinimumThreshold}
      />

      <WeightedFreshPopup
        showWeightedFreshModal={showWeightedFreshModal}
        setShowWeightedFreshModal={setShowWeightedFreshModal}
        coverImageURL={coverImageURL}
        title={title}
        productType={productType}
        weight={weight}
        priceUnit={priceUnit}
        originPrice={originPrice}
        displayPrice={displayPrice}
        promotedPrice={promotedPrice}
        upchargePercentage={upchargePercentage}
      />
    </StyledCard>
  );
};

BasketProduct.displayName = 'Product';

export default BasketProduct;
